import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { markEmailAsCaptured } from '@/pages/jdv2/JobDetailV2ApplyModal';
import { siteEvents } from '@/lib/siteEvents';
import { ALERT_PLACE } from '@/lib/alertPlacement';
import './job-alert-signup.css';

/**
 * "Get new <role> jobs by email" on job pages.
 *
 * Why: in the week to 6 October, 186 of 212 new job alerts came from the
 * email step inside the Apply flow. About seven in ten job viewers never
 * click Apply, so they never saw an offer to hear about the next role, and
 * the standalone alert path produced 26 alerts from 11 people, almost all
 * signed in. This puts the offer on the page for everyone.
 *
 * It calls create-job-alert, the same function as the board's alert form:
 * it drops duplicates, sends the welcome email and the Slack ping, and the
 * tracker counts it as an `alert` conversion on the job page. The alert is
 * for the job's canonical role (not its exact title, which would match
 * almost nothing) in its location, or remote when the job is remote.
 *
 * Three placements: `rail` sits in the sticky sidebar on desktop, `inline`
 * sits after the job summary on phones (where the sidebar falls below the
 * whole description), and `closed` sits inside the closed-job notice.
 *
 * The offer (2026-10-09). In its first three days about 1,470 sessions saw
 * the form and 7 signed up. "Get new Senior Accountant jobs by email" does
 * not say whether there will be any, so the form now says how many there
 * were: "21 new Senior Accountant jobs in Dallas this week". The number comes
 * from job_alert_preview(), which runs the alert's own search, and the alert
 * is created for the place the number is about (the metro, else the state),
 * so the count and the emails describe the same thing. About 6 in 10
 * listings have a number; the rest keep the plain wording.
 *
 * It ran as a split until 2026-11-03: half of visits (arm `count`) saw the
 * number, half (arm `plain`) the old wording. Both arms fetch the number, so the readout
 * compares like with like: visits where a number existed, shown or not.
 * `alert_form_seen` and `alert_form_submit` carry the arm and the number. To
 * end the test, make ARM a constant.
 */

export interface AlertJob {
  id: string;
  title: string;
  location?: string | null;
  is_remote?: boolean | null;
  canonical_role?: string | null;
  hours?: string | null;
}
type Variant = 'rail' | 'inline' | 'closed';

/** What job_alert_preview() answers. `n` is null when no place has 3 jobs. */
interface Preview {
  role: string;
  scope: 'metro' | 'state' | 'country' | 'remote' | null;
  place: string | null;
  /** The location to store the alert with, so it searches what `n` counted. */
  location: string | null;
  n: number | null;
  window: 'week' | 'month' | null;
}

// The count/plain test ended 2026-11-03: from Oct 20 to Nov 3 the two arms
// signed up at the same rate (the number made no difference), so every visit
// now sees the number when there is one. `arm` stays in the events so the
// readout keeps one shape.
const ARM: 'count' | 'plain' = 'count';

/** How long the number may take before the form shows without it. */
const PREVIEW_WAIT_MS = 1500;

function usePreview(jobId: string) {
  return useQuery({
    queryKey: ['job_alert_preview', jobId],
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as unknown as (fn: string, a: object) => Promise<{ data: unknown; error: unknown }>)(
        'job_alert_preview', { p_job_id: jobId });
      if (error) throw error;
      return (data ?? null) as Preview | null;
    },
    enabled: !!jobId,
    staleTime: 1000 * 60 * 30,
    retry: false,
  });
}

/** "the United States", "Canada", "Texas", "Dallas". */
function inPlace(place: string): string {
  return /^(United |Netherlands$|Philippines$|Bahamas$|Dominican Republic$|Czech Republic$)/.test(place) ? `the ${place}` : place;
}

function useRoleName(slug: string | null | undefined) {
  const { data } = useQuery({
    queryKey: ['canonical_role_name', slug],
    queryFn: async () => {
      const { data } = await supabase.from('canonical_roles').select('display_name').eq('slug', slug!).maybeSingle();
      return (data as { display_name?: string } | null)?.display_name ?? null;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 60,
  });
  return data ?? null;
}

/** "San Francisco, CALIFORNIA, US" -> "San Francisco, California". */
function placeName(loc: string): string {
  return loc
    .replace(/,\s*(US|USA|United States)\s*$/i, '')
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => (p === p.toUpperCase() && p.length > 3 ? p.charAt(0) + p.slice(1).toLowerCase() : p))
    .join(', ');
}

export function JobAlertSignup({ job, variant }: { job: AlertJob; variant: Variant }) {
  const { session } = useAuth();
  const role = useRoleName(job.canonical_role);
  const [email, setEmail] = React.useState('');
  const [state, setState] = React.useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const ref = React.useRef<HTMLElement | null>(null);
  const inputId = `jd-alert-email-${variant}`;

  // The number, or the knowledge that there is none. A slow or failed answer
  // must not hold the form back, so it stops waiting after PREVIEW_WAIT_MS.
  const preview = usePreview(job.id);
  const [waited, setWaited] = React.useState(false);
  React.useEffect(() => {
    setWaited(false);
    const t = window.setTimeout(() => setWaited(true), PREVIEW_WAIT_MS);
    return () => window.clearTimeout(t);
  }, [job.id]);
  const settled = preview.isFetched || preview.isError || waited;
  const found = preview.data && preview.data.n ? preview.data : null;
  const offer = ARM === 'count' ? found : null;
  // What the reader was shown, and what they could have been shown.
  const seen = React.useMemo(
    () => ({ variant, arm: ARM, place: ALERT_PLACE, n: found?.n ?? null, scope: found?.scope ?? null, win: found?.window ?? null }),
    [variant, found?.n, found?.scope, found?.window],
  );

  React.useEffect(() => { setState('idle'); }, [job.id]);

  // One sighting per job and placement, so the rate can be read per placement.
  // It waits for the number so the sighting says whether one was on offer.
  React.useEffect(() => {
    const el = ref.current;
    if (!settled || !el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        siteEvents.act('alert_form_seen', seen);
        io.disconnect();
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
    // `seen` is read once the number has settled; it does not change after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id, variant, settled]);

  const remote = !!job.is_remote;
  const what = role ? `${role} jobs` : 'jobs like this';
  const where = remote ? 'remote ' : '';
  const place = !remote && job.location ? placeName(job.location) : '';
  const hours = (job.hours ?? '').toLowerCase();
  // With a number on show, the alert is for the place the number is about.
  const alertLocation = offer && !remote ? offer.location ?? job.location : job.location;

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (state === 'sending') return;
    if (!session && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setState('error'); return; }
    setState('sending');
    try {
      const { error } = await supabase.functions.invoke('create-job-alert', {
        body: {
          email: session ? undefined : email.trim(),
          jobTitle: role ?? job.title,
          location: remote ? undefined : alertLocation ?? undefined,
          isRemote: remote,
          isPartTime: hours.includes('part'),
          isContract: hours.includes('contract'),
          isInternship: hours.includes('intern'),
          frequency: 'daily',
          source: `job_detail_${variant}`,
        },
      });
      if (error) throw error;
      markEmailAsCaptured();
      siteEvents.act('alert_form_submit', seen);
      setState('done');
    } catch {
      setState('error');
    }
  };

  const offerPlace = offer && offer.scope !== 'remote' && offer.place ? ` in ${inPlace(offer.place)}` : '';
  const heading = offer
    ? `${offer.n!.toLocaleString('en-US')} new ${where}${offer.role} jobs${offerPlace} ${offer.window === 'week' ? 'this week' : 'in the last 30 days'}`
    : `Get new ${where}${what} by email`;
  const sub = offer
    ? 'Get the next ones by email. One email a day, unsubscribe in one click.'
    : place
      ? `One email a day when new ones open in ${place}. Unsubscribe in one click.`
      : `One email a day when new ones open. Unsubscribe in one click.`;
  const done = offer
    ? `Done. We will email you when new ${where}${offer.role} jobs open${offerPlace}.`
    : `Done. We will email you when new ${where}${what} are posted.`;
  // The count arm holds its words until the number is in, so the heading
  // does not change under the reader. The plain arm has nothing to wait for.
  const pending = ARM === 'count' && !settled;

  return (
    <section
      ref={ref}
      className={`jd-alert jd-alert--${variant}${pending ? ' is-pending' : ''}`}
      aria-labelledby={`jd-alert-title-${variant}`}
      aria-busy={pending || undefined}
    >
      <h2 className="jd-alert__title" id={`jd-alert-title-${variant}`}>{heading}</h2>
      {state === 'done' ? (
        <p className="jd-alert__done" role="status">{done}</p>
      ) : (
        <>
          <p className="jd-alert__sub">{sub}</p>
          {session ? (
            <button type="button" className="jd-alert__btn" onClick={() => submit()} disabled={state === 'sending'}>
              {state === 'sending' ? 'Setting it up...' : 'Email me these jobs'}
            </button>
          ) : (
            <form className="jd-alert__form" onSubmit={submit} noValidate>
              <label className="jd-alert__label" htmlFor={inputId}>Email address</label>
              <input
                id={inputId}
                className="jd-alert__input"
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (state === 'error') setState('idle'); }}
                aria-invalid={state === 'error'}
                aria-describedby={state === 'error' ? `${inputId}-err` : undefined}
                required
              />
              <button type="submit" className="jd-alert__btn" disabled={state === 'sending'}>
                {state === 'sending' ? 'Setting it up...' : 'Email me'}
              </button>
            </form>
          )}
          {state === 'error' && (
            <p className="jd-alert__err" id={`${inputId}-err`} role="alert">
              That did not go through. Check the email address and try again.
            </p>
          )}
        </>
      )}
    </section>
  );
}
